/**
 * User Profile Page
 *
 * Our own profile form (name, phone → PATCH /api/v1/users/profile)
 * + Clerk <UserProfile /> for MFA management only.
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { UserProfile as ClerkUserProfile } from '@clerk/react'
import { api, ApiError } from '@/services/api'
import { Card, Button, Input } from '@/components/ui'

export function UserProfilePage() {
  const queryClient = useQueryClient()

  const { data: profile, isLoading } = useQuery({
    queryKey: ['user-profile'],
    queryFn: () => api.getUserProfile(),
  })

  const updateMutation = useMutation({
    mutationFn: (data: {
      firstName?: string
      lastName?: string
      phone?: string
      mobilePhone?: string
      receiveNotifications?: boolean
    }) => api.updateUserProfile(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['user-profile'] })
    },
  })

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-gray-200 rounded w-48" />
        <div className="bg-white shadow rounded-lg p-6 h-64" />
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
        <Card title="Profile Not Found">
          <p className="text-gray-600">
            Your user profile could not be loaded. Please try again.
          </p>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Profile</h1>

      <ProfileForm
        profile={{
          email: profile.email,
          firstName: profile.firstName ?? null,
          lastName: profile.lastName ?? null,
          phone: profile.phone ?? null,
          mobilePhone: profile.mobilePhone ?? null,
          receiveNotifications: profile.receiveNotifications,
        }}
        onSave={(data) => { updateMutation.mutate(data); }}
        isLoading={updateMutation.isPending}
        error={
          updateMutation.error instanceof ApiError
            ? updateMutation.error.message
            : undefined
        }
        isSuccess={updateMutation.isSuccess}
      />

      {/* MFA Management via Clerk */}
      <Card title="Security" description="Manage multi-factor authentication">
        <div className="clerk-profile-wrapper">
          <ClerkUserProfile
            routing="path"
            path="/profile"
          />
        </div>
      </Card>
    </div>
  )
}

function ProfileForm({
  profile,
  onSave,
  isLoading,
  error,
  isSuccess,
}: {
  profile: {
    email: string
    firstName: string | null
    lastName: string | null
    phone: string | null
    mobilePhone: string | null
    receiveNotifications: boolean
  }
  onSave: (data: {
    firstName?: string
    lastName?: string
    phone?: string
    mobilePhone?: string
    receiveNotifications?: boolean
  }) => void
  isLoading: boolean
  error?: string
  isSuccess: boolean
}) {
  const [firstName, setFirstName] = useState(profile.firstName ?? '')
  const [lastName, setLastName] = useState(profile.lastName ?? '')
  const [phone, setPhone] = useState(profile.phone ?? '')
  const [mobilePhone, setMobilePhone] = useState(profile.mobilePhone ?? '')
  const [receiveNotifications, setReceiveNotifications] = useState(
    profile.receiveNotifications
  )

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave({
      firstName: firstName || undefined,
      lastName: lastName || undefined,
      phone: phone || undefined,
      mobilePhone: mobilePhone || undefined,
      receiveNotifications,
    })
  }

  return (
    <Card title="Personal Information" description="Update your profile details">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Email"
          value={profile.email}
          disabled
          helperText="Email is managed by your authentication provider"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="First Name"
            value={firstName}
            onChange={(e) => { setFirstName(e.target.value); }}
            placeholder="John"
          />
          <Input
            label="Last Name"
            value={lastName}
            onChange={(e) => { setLastName(e.target.value); }}
            placeholder="Doe"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Phone"
            type="tel"
            value={phone}
            onChange={(e) => { setPhone(e.target.value); }}
            placeholder="+1 (555) 123-4567"
          />
          <Input
            label="Mobile Phone"
            type="tel"
            value={mobilePhone}
            onChange={(e) => { setMobilePhone(e.target.value); }}
            placeholder="+1 (555) 987-6543"
          />
        </div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={receiveNotifications}
            onChange={(e) => { setReceiveNotifications(e.target.checked); }}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-gray-700">
            Receive email notifications for alerts
          </span>
        </label>

        {error && (
          <p className="text-sm text-danger-600">{error}</p>
        )}
        {isSuccess && (
          <p className="text-sm text-success-600">Profile updated successfully.</p>
        )}

        <div className="flex justify-end">
          <Button type="submit" isLoading={isLoading}>
            Save Changes
          </Button>
        </div>
      </form>
    </Card>
  )
}
